import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import AdminLogin from './AdminLogin';
import './AdminPanel.css';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastFetched, setLastFetched] = useState(null);

  // Check if user is authenticated on component mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // Handle successful login
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setLeads([]);
    setLastFetched(null);
  };

  // Fetch leads from backend
  const fetchLeads = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.get('/api/leads');
      setLeads(response.data.leads || []);
      setLastFetched(new Date().toLocaleString());
    } catch (err) {
      setError(
        err.response?.data?.error || 
        'Failed to fetch leads. Please try again.'
      );
      setLeads([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="admin-panel">
      <div className="container">
        <div className="admin-header">
          <h1>Jasper Group Estate - Admin Panel</h1>
          <p>Manage and view all submitted leads</p>
          <button 
            onClick={handleLogout} 
            className="btn btn-secondary logout-btn"
          >
            Logout
          </button>
        </div>

        <div className="admin-actions">
          <button 
            onClick={fetchLeads} 
            className="btn btn-primary fetch-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Fetching Leads...' : 'Fetch Leads'}
          </button>
          
          {lastFetched && (
            <p className="last-fetched">
              Last fetched: {lastFetched}
            </p>
          )}
        </div>

        {error && (
          <div className="alert alert-error">
            ✗ {error}
          </div>
        )}

        {leads.length > 0 && (
          <div className="leads-container">
            <div className="leads-header">
              <h2>Leads ({leads.length})</h2>
            </div>
            
            <div className="leads-table-container">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Property Interest</th>
                    <th>Submitted Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead._id}>
                      <td>{lead.name}</td>
                      <td>{lead.email}</td>
                      <td>{lead.phoneNumber}</td>
                      <td>
                        <span className="property-badge">
                          {lead.propertyInterest}
                        </span>
                      </td>
                      <td>
                        {new Date(lead.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {leads.length === 0 && !isLoading && !error && lastFetched && (
          <div className="no-leads">
            <p>No leads found.</p>
          </div>
        )}

        {leads.length === 0 && !isLoading && !error && !lastFetched && (
          <div className="no-leads">
            <p>Click "Fetch Leads" to retrieve all submitted leads.</p>
          </div>
        )}

        <div className="admin-footer">
          <a href="/" className="back-link">← Back to Landing Page</a>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;

